// ═══════════════════════════════════════════════════════════
// ARAMS Dashboard — App Router
// ═══════════════════════════════════════════════════════════

import { Routes, Route, Navigate } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { store, setAuth } from '@/lib/store';
import { api } from '@/lib/api';
import { useWebSocket } from '@/lib/ws';

import DashboardLayout from '@/components/layout/DashboardLayout';
import LandingPage from '@/pages/LandingPage';
import LoginPage from '@/pages/LoginPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import ProjectsPage from '@/pages/ProjectsPage';
import ProjectFormPage from '@/pages/ProjectFormPage';
import ProjectDetailPage from '@/pages/ProjectDetailPage';
import RunDetailPage from '@/pages/RunDetailPage';
import AIAgentPage from '@/pages/AIAgentPage';
import AnalyticsPage from '@/pages/AnalyticsPage';
import SettingsPage from '@/pages/SettingsPage';
import AdminPage from '@/pages/AdminPage';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const snap = useSnapshot(store);
  if (!snap.token) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export default function App() {
  const snap = useSnapshot(store);

  // Auto-load user from token
  const { data: user } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.getMe(),
    enabled: !!snap.token && !snap.user,
    retry: false,
  });

  useEffect(() => {
    if (user && snap.token) {
      setAuth(user, snap.token);
    }
  }, [user, snap.token]);

  // WebSocket connection
  useWebSocket();

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Routes>
                <Route path="/" element={<Navigate to="/projects" replace />} />
                <Route path="/projects" element={<ProjectsPage />} />
                <Route path="/projects/new" element={<ProjectFormPage />} />
                <Route path="/projects/:id" element={<ProjectDetailPage />} />
                <Route path="/projects/:id/runs/:runId" element={<RunDetailPage />} />
                <Route path="/projects/:id/ai" element={<AIAgentPage />} />
                <Route path="/projects/:id/analytics" element={<AnalyticsPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/admin" element={<AdminPage />} />
              </Routes>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}
